import apiClient from '../../config/axiosConfig';
import { API_BASE_URL } from '../../utils/apiUtils';

// ============================================================================
// VALIDATION FUNCTIONS
// ============================================================================

const validateTeamId = (teamId) => {
    if (!teamId || typeof teamId !== 'string' || teamId.trim() === '') {
        throw new Error('Team ID is required and must be a valid string');
    }
    return teamId.trim();
};

const validateTemplateId = (templateId) => {
    if (!templateId || typeof templateId !== 'string' || templateId.trim() === '') {
        throw new Error('Template ID is required and must be a valid string');
    }
    return templateId.trim();
};

const validateTemplateName = (name) => {
    if (!name || typeof name !== 'string' || name.trim() === '') {
        throw new Error('Template name is required');
    }
    if (name.trim().length > 100) {
        throw new Error('Template name cannot exceed 100 characters');
    }
    return name.trim();
};

// ============================================================================
// ERROR HANDLER
// ============================================================================

const handleTemplateApiError = (error, operation = 'Template API operation') => {
    console.error(`[TeamTemplateAPI] ${operation} Error:`, {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
        requestUrl: error.config?.url,
        requestMethod: error.config?.method
    });

    if (error.response?.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('authUser');
        throw new Error('Your session has expired. Please log in again');
    }

    if (error.response?.status === 403) {
        throw new Error('You do not have permission to perform this action');
    }

    if (error.response?.status === 404) {
        throw new Error('Template not found');
    }

    if (error.response?.status === 409) {
        throw new Error('A template with this name already exists for this team');
    }

    if (error.response?.status >= 500) {
        throw new Error('Server error. Please try again later.');
    }

    if (error.code === 'ERR_NETWORK') {
        throw new Error('Network error. Please check your connection and try again.');
    }

    if (error.code === 'ECONNABORTED') {
        throw new Error('Request timeout. Please try again.');
    }

    const errorMessage = error.response?.data?.message || error.message || 'An unexpected error occurred';
    throw new Error(errorMessage);
};

// ============================================================================
// TEMPLATE CRUD OPERATIONS
// ============================================================================

/**
 * Create a new team template
 * POST /api/team/:teamId/templates
 * @param {string} teamId - Team ID
 * @param {Object} templateData - Template data
 * @returns {Promise<Object>} Created template data
 */
export const createTemplate = async (teamId, templateData) => {
    try {
        const validTeamId = validateTeamId(teamId);
        const validTemplateName = validateTemplateName(templateData.templateName);

        console.log('[TeamTemplateAPI] Creating template:', validTemplateName);

        const payload = {
            templateName: validTemplateName,
            about: templateData.about || {},
            links: templateData.links || {},
            qrCode: templateData.qrCode || {},
            leadCapture: templateData.leadCapture || {},
            followUpEmail: templateData.followUpEmail || {},
            settings: templateData.settings || {},
            isDefault: templateData.isDefault || false
        };

        // 🔍 CRITICAL: Log ALL template data being sent to backend
        console.log('═══════════════════════════════════════════════════════════');
        console.log('[TeamTemplateAPI] 📤 CREATING TEMPLATE - Full Payload:');
        console.log('═══════════════════════════════════════════════════════════');
        console.log('Team ID:', validTeamId);
        console.log('Template Name:', payload.templateName);
        console.log('\n📋 ABOUT SECTION:');
        console.log(JSON.stringify(payload.about, null, 2));
        console.log('\n🖼️  IMAGE URLs in ABOUT:');
        console.log('  - profilePictureUrl:', payload.about?.profilePictureUrl);
        console.log('  - coverPhotoUrl:', payload.about?.coverPhotoUrl);
        console.log('  - companyLogoUrl:', payload.about?.companyLogoUrl);
        console.log('\n🔍 IMAGE URL VALIDATION:');
        console.log('  - profilePictureUrl type:', typeof payload.about?.profilePictureUrl);
        console.log('  - profilePictureUrl is null:', payload.about?.profilePictureUrl === null);
        console.log('  - profilePictureUrl is undefined:', payload.about?.profilePictureUrl === undefined);
        console.log('  - profilePictureUrl is string:', typeof payload.about?.profilePictureUrl === 'string');
        console.log('  - profilePictureUrl is Firebase:', payload.about?.profilePictureUrl && typeof payload.about?.profilePictureUrl === 'string' && !payload.about?.profilePictureUrl.startsWith('blob:'));
        console.log('  - profilePictureUrl is blob:', payload.about?.profilePictureUrl?.startsWith('blob:'));
        console.log('  - profilePictureUrl length:', payload.about?.profilePictureUrl?.length);
        console.log('  - coverPhotoUrl type:', typeof payload.about?.coverPhotoUrl);
        console.log('  - coverPhotoUrl is Firebase:', payload.about?.coverPhotoUrl && typeof payload.about?.coverPhotoUrl === 'string' && !payload.about?.coverPhotoUrl.startsWith('blob:'));
        console.log('  - companyLogoUrl type:', typeof payload.about?.companyLogoUrl);
        console.log('  - companyLogoUrl is Firebase:', payload.about?.companyLogoUrl && typeof payload.about?.companyLogoUrl === 'string' && !payload.about?.companyLogoUrl.startsWith('blob:'));
        console.log('\n📦 FULL PAYLOAD (all sections):');
        console.log(JSON.stringify(payload, null, 2));
        console.log('═══════════════════════════════════════════════════════════');

        const response = await apiClient.post(
            `${API_BASE_URL}/api/team/${validTeamId}/templates`,
            payload
        );

        console.log('[TeamTemplateAPI] ✅ Template created successfully');
        console.log('[TeamTemplateAPI] Response template image URLs:', {
            profilePictureUrl: response.data?.template?.about?.profilePictureUrl,
            coverPhotoUrl: response.data?.template?.about?.coverPhotoUrl,
            companyLogoUrl: response.data?.template?.about?.companyLogoUrl
        });
        return response.data;
    } catch (error) {
        console.error('[TeamTemplateAPI] Error creating template:', error);
        handleTemplateApiError(error, 'Creating template');
    }
};

/**
 * Get all templates for a team
 * GET /api/team/:teamId/templates
 * @param {string} teamId - Team ID
 * @param {Object} options - Query options (page, limit)
 * @returns {Promise<Object>} Templates list with pagination
 */
export const getTemplates = async (teamId, options = {}) => {
    try {
        const validTeamId = validateTeamId(teamId);
        const { page = 1, limit = 50 } = options;

        console.log('[TeamTemplateAPI] Fetching templates for team:', validTeamId);

        const response = await apiClient.get(
            `${API_BASE_URL}/api/team/${validTeamId}/templates`,
            {
                params: { page, limit }
            }
        );

        console.log('[TeamTemplateAPI] Templates fetched successfully');
        return response.data;
    } catch (error) {
        console.error('[TeamTemplateAPI] Error fetching templates:', error);
        handleTemplateApiError(error, 'Fetching templates');
    }
};

/**
 * Get a single template
 * GET /api/team/:teamId/templates/:templateId
 * @param {string} teamId - Team ID
 * @param {string} templateId - Template ID
 * @returns {Promise<Object>} Template data
 */
export const getTemplate = async (teamId, templateId) => {
    try {
        const validTeamId = validateTeamId(teamId);
        const validTemplateId = validateTemplateId(templateId);

        console.log('[TeamTemplateAPI] Fetching template:', validTemplateId);

        const response = await apiClient.get(
            `${API_BASE_URL}/api/team/${validTeamId}/templates/${validTemplateId}`
        );

        console.log('[TeamTemplateAPI] Template fetched successfully');
        return response.data;
    } catch (error) {
        console.error('[TeamTemplateAPI] Error fetching template:', error);
        handleTemplateApiError(error, 'Fetching template');
    }
};

/**
 * Update a template
 * PUT /api/team/:teamId/templates/:templateId
 * @param {string} teamId - Team ID
 * @param {string} templateId - Template ID
 * @param {Object} updateData - Update data
 * @returns {Promise<Object>} Updated template data
 */
export const updateTemplate = async (teamId, templateId, updateData) => {
    try {
        const validTeamId = validateTeamId(teamId);
        const validTemplateId = validateTemplateId(templateId);

        const payload = {};

        if (updateData.templateName !== undefined) {
            payload.templateName = validateTemplateName(updateData.templateName);
        }
        if (updateData.about !== undefined) {
            payload.about = updateData.about;
        }
        if (updateData.links !== undefined) payload.links = updateData.links;
        if (updateData.qrCode !== undefined) payload.qrCode = updateData.qrCode;
        if (updateData.leadCapture !== undefined) payload.leadCapture = updateData.leadCapture;
        if (updateData.followUpEmail !== undefined) payload.followUpEmail = updateData.followUpEmail;
        if (updateData.settings !== undefined) payload.settings = updateData.settings;
        if (updateData.isDefault !== undefined) payload.isDefault = updateData.isDefault;

        // 🔍 CRITICAL: Log ALL template data being sent to backend
        console.log('═══════════════════════════════════════════════════════════');
        console.log('[TeamTemplateAPI] 📤 UPDATING TEMPLATE - Full Payload:');
        console.log('═══════════════════════════════════════════════════════════');
        console.log('Team ID:', validTeamId);
        console.log('Template ID:', validTemplateId);
        if (payload.templateName) {
            console.log('Template Name:', payload.templateName);
        }
        console.log('\n📋 ABOUT SECTION:');
        console.log(JSON.stringify(payload.about, null, 2));
        console.log('\n🖼️  IMAGE URLs in ABOUT:');
        console.log('  - profilePictureUrl:', payload.about?.profilePictureUrl);
        console.log('  - coverPhotoUrl:', payload.about?.coverPhotoUrl);
        console.log('  - companyLogoUrl:', payload.about?.companyLogoUrl);
        console.log('\n🔍 IMAGE URL VALIDATION:');
        console.log('  - profilePictureUrl type:', typeof payload.about?.profilePictureUrl);
        console.log('  - profilePictureUrl is null:', payload.about?.profilePictureUrl === null);
        console.log('  - profilePictureUrl is undefined:', payload.about?.profilePictureUrl === undefined);
        console.log('  - profilePictureUrl is string:', typeof payload.about?.profilePictureUrl === 'string');
        console.log('  - profilePictureUrl is Firebase:', payload.about?.profilePictureUrl && typeof payload.about?.profilePictureUrl === 'string' && !payload.about?.profilePictureUrl.startsWith('blob:'));
        console.log('  - profilePictureUrl is blob:', payload.about?.profilePictureUrl?.startsWith('blob:'));
        console.log('  - profilePictureUrl length:', payload.about?.profilePictureUrl?.length);
        console.log('  - coverPhotoUrl type:', typeof payload.about?.coverPhotoUrl);
        console.log('  - coverPhotoUrl is Firebase:', payload.about?.coverPhotoUrl && typeof payload.about?.coverPhotoUrl === 'string' && !payload.about?.coverPhotoUrl.startsWith('blob:'));
        console.log('  - companyLogoUrl type:', typeof payload.about?.companyLogoUrl);
        console.log('  - companyLogoUrl is Firebase:', payload.about?.companyLogoUrl && typeof payload.about?.companyLogoUrl === 'string' && !payload.about?.companyLogoUrl.startsWith('blob:'));
        console.log('\n📦 FULL PAYLOAD (all sections):');
        console.log(JSON.stringify(payload, null, 2));
        console.log('\n📥 ORIGINAL UPDATE DATA (for comparison):');
        console.log('  - updateData.about?.profilePictureUrl:', updateData.about?.profilePictureUrl);
        console.log('  - updateData.about?.coverPhotoUrl:', updateData.about?.coverPhotoUrl);
        console.log('  - updateData.about?.companyLogoUrl:', updateData.about?.companyLogoUrl);
        console.log('═══════════════════════════════════════════════════════════');

        const response = await apiClient.put(
            `${API_BASE_URL}/api/team/${validTeamId}/templates/${validTemplateId}`,
            payload
        );

        console.log('[TeamTemplateAPI] ✅ Template updated successfully');
        console.log('[TeamTemplateAPI] Response template image URLs:', {
            profilePictureUrl: response.data?.template?.about?.profilePictureUrl,
            coverPhotoUrl: response.data?.template?.about?.coverPhotoUrl,
            companyLogoUrl: response.data?.template?.about?.companyLogoUrl
        });
        return response.data;
    } catch (error) {
        console.error('[TeamTemplateAPI] ❌ Error updating template:', error);
        handleTemplateApiError(error, 'Updating template');
    }
};

/**
 * Delete a template
 * DELETE /api/team/:teamId/templates/:templateId
 * @param {string} teamId - Team ID
 * @param {string} templateId - Template ID
 * @returns {Promise<Object>} Deletion confirmation
 */
export const deleteTemplate = async (teamId, templateId) => {
    try {
        const validTeamId = validateTeamId(teamId);
        const validTemplateId = validateTemplateId(templateId);

        console.log('[TeamTemplateAPI] Deleting template:', validTemplateId);

        const response = await apiClient.delete(
            `${API_BASE_URL}/api/team/${validTeamId}/templates/${validTemplateId}`
        );

        console.log('[TeamTemplateAPI] Template deleted successfully');
        return response.data;
    } catch (error) {
        console.error('[TeamTemplateAPI] Error deleting template:', error);
        handleTemplateApiError(error, 'Deleting template');
    }
};

/**
 * Assign/unassign members to a template
 * PATCH /api/team/:teamId/templates/:templateId/assign-members
 * @param {string} teamId - Team ID
 * @param {string} templateId - Template ID
 * @param {Object} assignmentData - { assignMemberIds: [], unassignMemberIds: [], setAsDefault: boolean }
 * @returns {Promise<Object>} Assignment result
 */
export const assignMembersToTemplate = async (teamId, templateId, assignmentData) => {
    try {
        const validTeamId = validateTeamId(teamId);
        const validTemplateId = validateTemplateId(templateId);

        const { assignMemberIds = [], unassignMemberIds = [], setAsDefault = false } = assignmentData;

        if (!Array.isArray(assignMemberIds) || !Array.isArray(unassignMemberIds)) {
            throw new Error('assignMemberIds and unassignMemberIds must be arrays');
        }

        console.log('[TeamTemplateAPI] Assigning members to template:', {
            templateId: validTemplateId,
            assignCount: assignMemberIds.length,
            unassignCount: unassignMemberIds.length,
            setAsDefault
        });

        const response = await apiClient.patch(
            `${API_BASE_URL}/api/team/${validTeamId}/templates/${validTemplateId}/assign-members`,
            {
                assignMemberIds,
                unassignMemberIds,
                setAsDefault
            }
        );

        console.log('[TeamTemplateAPI] Members assigned successfully');
        return response.data;
    } catch (error) {
        console.error('[TeamTemplateAPI] Error assigning members:', error);
        handleTemplateApiError(error, 'Assigning members to template');
    }
};

/**
 * Apply template to a member's card(s)
 * POST /api/team/:teamId/templates/:templateId/apply
 * @param {string} teamId - Team ID
 * @param {string} templateId - Template ID
 * @param {Object} applyData - { memberId: string, cardId?: string }
 * @returns {Promise<Object>} Application result
 */
export const applyTemplateToMember = async (teamId, templateId, applyData) => {
    try {
        const validTeamId = validateTeamId(teamId);
        const validTemplateId = validateTemplateId(templateId);

        const { memberId, cardId } = applyData;

        if (!memberId) {
            throw new Error('memberId is required');
        }

        console.log('[TeamTemplateAPI] Applying template to member:', {
            templateId: validTemplateId,
            memberId,
            cardId
        });

        const response = await apiClient.post(
            `${API_BASE_URL}/api/team/${validTeamId}/templates/${validTemplateId}/apply`,
            {
                memberId,
                cardId
            }
        );

        console.log('[TeamTemplateAPI] Template applied successfully');
        return response.data;
    } catch (error) {
        console.error('[TeamTemplateAPI] Error applying template:', error);
        handleTemplateApiError(error, 'Applying template to member');
    }
};


